import React, { useState, useRef } from 'react';
import { StageBlueprint, Venue, VenuePackage } from '../../types';
import { ConicGlowCard } from '../common/ConicGlowCard';
import {
  SparklesIcon,
  UsersIcon,
  MapPinIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CloseIcon,
  CalendarIcon,
  StarIcon,
  TrophyIcon,
  ZapIcon
} from '../common/Icons';

export type MenuTierKey = 'chicken_1dish' | 'mutton_royal' | 'beef_degi' | 'flat_rental';

interface PriceComparisonDeckProps {
  venues?: Venue[];
  allVenues?: Venue[];
  guestCount?: number;
  initialMenuTier?: MenuTierKey;
  initialIncludeDecorAddon?: boolean;
  initialIncludeGenFuel?: boolean;
  focusMetrics?: string[];
  voiceSpotlightVenueId?: string | null;
  blueprint?: StageBlueprint | null;
  relaxationNotice?: string | null;
  onSelectVenue?: (venue: Venue, pkg?: VenuePackage) => void;
  onBack?: () => void;
  onTouchAction?: (action: string, data?: any) => void;
}

export const PriceComparisonDeck: React.FC<PriceComparisonDeckProps> = React.memo(({
  venues,
  focusMetrics = [],
  voiceSpotlightVenueId = null,
  blueprint,
  relaxationNotice,
  onSelectVenue,
  onBack,
  onTouchAction
}) => {
  const [focusedVenueId, setFocusedVenueId] = useState<string | null>(null);
  const [highlightedVenueId, setHighlightedVenueId] = useState<string | null>(null);
  const lastTouchMarkerRef = useRef<{ id: string; time: number } | null>(null);
  const deckRootRef = useRef<HTMLDivElement | null>(null);

  const defaultCompareVenues: Venue[] = [
    {
      id: 'v-karsaz-01',
      slug: 'majestic-banquet-karsaz',
      name: 'Majestic Banquet',
      area: 'Karsaz',
      address: 'Main Karsaz Road, Karachi',
      lat: 24.8821,
      lng: 67.0892,
      venueType: 'banquet_hall',
      minCapacity: 250,
      maxCapacity: 800,
      googleRating: 4.8,
      googleReviewCount: 342,
      isClaimed: true,
      coverPhotoUrl: '/uploads/venues/majestic-banquet-karsaz/photo_0.webp',
      packages: [
        { id: 'p1', packageType: 'flat_rental_peak', name: 'Peak Season Hall Rental', pricePkr: 330000, minGuests: 250, menuItems: ['Main Pillarless Hall', 'Central Chiller AC', 'Standby Generator Backup', 'Valet Parking & Bridal Suite'] }
      ]
    },
    {
      id: 'v-dha-03',
      slug: 'defence-authority-creek-club-dha',
      name: 'Defence Authority Creek Club',
      area: 'DHA/Clifton',
      address: 'DHA Waterfront, Karachi',
      lat: 24.7812,
      lng: 67.0621,
      venueType: 'lawn',
      minCapacity: 300,
      maxCapacity: 1200,
      googleRating: 4.9,
      googleReviewCount: 512,
      isClaimed: true,
      coverPhotoUrl: '/uploads/venues/defence-authority-creek-club-dha/photo_0.webp',
      packages: [
        { id: 'p3', packageType: 'flat_rental_peak', name: 'Waterfront Lawn & Hall Rental', pricePkr: 420000, minGuests: 300, menuItems: ['Waterfront Open Lawn', 'Standby Generator Backup', 'Dedicated Valet Team', 'Bridal Suite'] }
      ]
    },
    {
      id: 'v-nn-01',
      slug: 'casablanca-banquet-north',
      name: 'Casablanca Banquet',
      area: 'North Nazimabad',
      address: 'Five Star Chowrangi Block H, Karachi',
      lat: 24.9381,
      lng: 67.0432,
      venueType: 'banquet_hall',
      minCapacity: 300,
      maxCapacity: 900,
      googleRating: 4.7,
      googleReviewCount: 289,
      isClaimed: false,
      coverPhotoUrl: '/uploads/venues/casablanca-banquet-north/photo_0.webp',
      packages: [
        { id: 'p5', packageType: 'flat_rental_peak', name: 'Standard Hall Rental', pricePkr: 275000, minGuests: 300, menuItems: ['Main Banquet Hall', 'Central Chiller AC', 'Standby Generator Backup', 'Valet Parking Lane'] }
      ]
    }
  ];

  const compareList = venues && venues.length >= 2 ? venues : defaultCompareVenues;

  const formatVenueType = (t?: string) => {
    if (!t) return 'Banquet Hall';
    return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const resolveCompareMetricValue = (v: Venue, metric: string, peakRental: number, offPeakRental: number): { label: string; value: string } => {
    const m = metric.toLowerCase().replace(/_/g, ' ');
    const cleanLabel = metric.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
    if (m.includes('gen') || m.includes('power') || m.includes('backup')) {
      return { label: cleanLabel, value: v.isClaimed ? 'Verified Standby Backup' : 'Standby Backup • Verify on Hold' };
    }
    if (m.includes('valet') || m.includes('parking')) {
      return { label: cleanLabel, value: v.isClaimed ? 'Dedicated Valet Lane' : 'Valet Available • Verify Capacity' };
    }
    if (m.includes('cap') || m.includes('guest') || m.includes('pax')) {
      return { label: cleanLabel, value: `${v.minCapacity}–${v.maxCapacity} Pax` };
    }
    if (m.includes('off') || m.includes('weekday')) {
      return { label: cleanLabel, value: `Rs. ${offPeakRental.toLocaleString()}` };
    }
    if (m.includes('price') || m.includes('rate') || m.includes('rental') || m.includes('cost')) {
      return { label: cleanLabel, value: `Rs. ${peakRental.toLocaleString()} flat` };
    }
    if (m.includes('ac') || m.includes('chill') || m.includes('climate')) {
      return { label: cleanLabel, value: 'Central Hall AC' };
    }
    if (m.includes('rating') || m.includes('review')) {
      return { label: cleanLabel, value: `${v.googleRating.toFixed(1)} (${v.googleReviewCount})` };
    }
    return { label: cleanLabel, value: 'Included' };
  };

  // Compute Pure Hall-Rental Breakdown per Venue (Zero Food/Catering Noise)
  const computedRentals = compareList.map((v) => {
    const uniquePackages = Array.from(
      new Map((v.packages || []).map((p) => [`${p.packageType}:${p.name}`, p])).values()
    );
    const flatPkg =
      uniquePackages.find(
        (p) => !p.packageType.includes('offpeak') && (p.packageType.includes('flat') || p.pricePkr >= 50000)
      ) ||
      uniquePackages.find(
        (p) => p.packageType.includes('flat') || p.pricePkr >= 50000
      );
    const offPeakPkg = uniquePackages.find((p) => p.packageType.includes('offpeak'));
    const fallbackPkg = uniquePackages[0];
    const baseBenchmark = fallbackPkg && fallbackPkg.pricePkr < 25000 ? fallbackPkg.pricePkr : 1850;

    const isGenericEstFlat = Boolean(
      flatPkg && ((flatPkg as any).isEstimated || /^estimated\s/i.test(String(flatPkg.name || '')))
    );
    const hasExplicitRental = Boolean(flatPkg && !isGenericEstFlat);

    let peakRentalPkr = flatPkg ? Number(flatPkg.pricePkr) : Math.round(baseBenchmark * 180);
    // Calibrate legacy uncalibrated bucket numbers if present in cached state
    if (isGenericEstFlat && [250000, 450000, 700000, 162500, 292500, 455000, 160000].includes(peakRentalPkr)) {
      const baseBucket = peakRentalPkr < 200000 ? 250000 : peakRentalPkr;
      const maxCap = Number(v.maxCapacity || 600);
      const rating = Number(v.googleRating || 4.3);
      const vType = String(v.venueType || 'banquet_hall').toLowerCase();
      const seedStr = String(v.slug || v.name || v.id || 'venue');
      let seedHash = 0;
      for (let i = 0; i < seedStr.length; i++) {
        seedHash = (seedHash + seedStr.charCodeAt(i) * (i + 1)) % 9973;
      }
      const capFactor = ((Math.min(1500, Math.max(250, maxCap)) - 550) / 1000) * 0.24;
      const ratingFactor = (Math.min(4.9, Math.max(3.8, rating)) - 4.3) * 0.18;
      const typeFactor =
        vType === 'hotel_ballroom' ? 0.22 : vType === 'marquee' ? 0.08 : vType === 'lawn' ? -0.05 : 0;
      const slugOffset = ((seedHash % 11) - 5) * 0.016;
      const mult = Math.min(1.55, Math.max(0.76, 1 + capFactor + ratingFactor + typeFactor + slugOffset));
      peakRentalPkr = Math.max(120000, Math.round((baseBucket * mult) / 5000) * 5000);
    }

    const rawOffPeak = offPeakPkg ? Number(offPeakPkg.pricePkr) : 0;
    const offPeakRentalPkr =
      rawOffPeak > 0 && rawOffPeak < peakRentalPkr && ![162500, 292500, 455000, 160000].includes(rawOffPeak)
        ? rawOffPeak
        : Math.max(85000, Math.round((peakRentalPkr * 0.72) / 5000) * 5000);

    const hallInclusions = [
      'Central Hall Air Conditioning & Lighting',
      v.isClaimed ? 'Verified Standby Generator Backup' : 'Standby Generator • Verify Fuel Terms on Hold',
      'Valet Parking Lane, Stage Platform & Bridal Suite'
    ];

    return {
      venue: v,
      activePkgObj: flatPkg || fallbackPkg,
      hasExplicitRental,
      peakRentalPkr,
      offPeakRentalPkr,
      hallInclusions
    };
  });

  const sortedRentals = [...computedRentals].sort(
    (a, b) =>
      a.peakRentalPkr - b.peakRentalPkr ||
      (b.venue.googleRating || 0) - (a.venue.googleRating || 0) ||
      (b.venue.googleReviewCount || 0) - (a.venue.googleReviewCount || 0)
  );
  const maxRental = Math.max(...computedRentals.map((c) => c.peakRentalPkr), 1);
  const minRental = Math.min(...computedRentals.map((c) => c.peakRentalPkr));
  const avgRental = Math.round(
    computedRentals.reduce((acc, c) => acc + c.peakRentalPkr, 0) / Math.max(computedRentals.length, 1)
  );
  const maxSavings = Math.max(0, maxRental - minRental);
  const focusedEntry = computedRentals.find((c) => c.venue.id === focusedVenueId) || null;

  const spectrumItems = (() => {
    const items = sortedRentals.map((c, idx) => {
      const span = Math.max(maxRental - minRental, 1);
      const tiedGroup = sortedRentals.filter((item) => item.peakRentalPkr === c.peakRentalPkr);
      const tiedIndex = sortedRentals
        .slice(0, idx)
        .filter((item) => item.peakRentalPkr === c.peakRentalPkr).length;
      const tiedOffset =
        tiedGroup.length > 1 ? (tiedIndex - (tiedGroup.length - 1) / 2) * 4.5 : 0;
      const rawPct =
        maxRental === minRental
          ? 50 + tiedOffset
          : Math.min(96, Math.max(4, ((c.peakRentalPkr - minRental) / span) * 88 + 6 + tiedOffset));
      const isLow = idx === 0 && c.peakRentalPkr === minRental;
      return {
        ...c,
        rankIndex: idx + 1,
        rawPct,
        isLow
      };
    });
    // Guarantee minimum 4.5% horizontal spacing between adjacent markers so near-tied prices never overlap
    for (let i = 1; i < items.length; i++) {
      if (items[i].rawPct - items[i - 1].rawPct < 4.5) {
        items[i].rawPct = Math.min(96, items[i - 1].rawPct + 4.5);
      }
    }
    return items;
  })();

  const scrollCardIntoHorizontalView = (venueId: string) => {
    if (typeof window === 'undefined' || !deckRootRef.current) return;
    const cardEl = deckRootRef.current.querySelector<HTMLElement>(
      `[data-comparison-venue-id="${venueId.replace(/"/g, '\\"')}"]`
    );
    if (cardEl && typeof cardEl.scrollIntoView === 'function') {
      cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  const activeSpectrumVenueId = highlightedVenueId || voiceSpotlightVenueId || null;
  const activeSpectrumItem = spectrumItems.find((item) => item.venue.id === activeSpectrumVenueId) || null;

  const formatSpectrumPkr = (val: number) => {
    if (val >= 1000000) {
      return `Rs. ${(val / 1000000).toFixed(val % 1000000 === 0 ? 0 : 2)}M`;
    }
    if (val >= 1000) {
      return `Rs. ${Math.round(val / 1000)}k`;
    }
    return `Rs. ${val.toLocaleString()}`;
  };

  const cleanTitle =
    blueprint?.cardViewMode === 'price_comparison' && blueprint?.title && !blueprint.title.includes('_')
      ? blueprint.title
      : 'Hall Rental Comparison';

  return (
    <div ref={deckRootRef} className="stage-deck-content spotlight-viewport-deck">
      {relaxationNotice && (
        <div className="relaxation-banner" role="status">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
            <SparklesIcon size={14} style={{ color: 'hsl(var(--accent-gold))' }} />
            <span>{relaxationNotice}</span>
          </div>
        </div>
      )}

      {/* Compact Single-Row Header */}
      <div className="deck-header" style={{ alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', minWidth: 0 }}>
          {onBack && (
            <button
              type="button"
              className="btn-secondary"
              onClick={onBack}
              style={{ padding: '0.32rem 0.68rem', fontSize: '0.74rem', flexShrink: 0 }}
            >
              <ArrowLeftIcon size={13} />
              <span>Back</span>
            </button>
          )}
          <h2 id="stage-active-heading" data-stage-heading="true" tabIndex={-1} className="deck-title" style={{ margin: 0 }}>
            {cleanTitle}
          </h2>
        </div>

        <div className="header-mobile-hide" style={{ fontSize: '0.75rem', color: 'hsl(var(--text-secondary))', fontWeight: 600, whiteSpace: 'nowrap' }}>
          Comparing Peak &amp; Off-Peak Flat Hall Rentals ({computedRentals.length} Venues{maxSavings > 0 ? ` • Up to Rs. ${maxSavings.toLocaleString()} Spread` : ''})
        </div>
      </div>

      {/* Google Flights "Price Spectrum Meter" with Interactive Card-to-Marker Connection */}
      <div
        className={`price-spectrum-meter ${activeSpectrumItem ? 'has-active-connection' : ''}`.trim()}
        role="region"
        aria-label="Karachi Hall Rental Price Spectrum Meter"
      >
        <div
          className="price-spectrum-labels"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.68rem',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            flexWrap: 'nowrap',
            gap: '0.35rem'
          }}
        >
          <span style={{ color: 'hsl(var(--accent-emerald-text))', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <TrophyIcon size={11} />
            <span className="num-tabular">Low: {formatSpectrumPkr(minRental)}</span>
          </span>
          {activeSpectrumItem ? (
            <span
              className="price-spectrum-active-summary num-tabular"
              style={{
                color: activeSpectrumItem.isLow ? 'hsl(var(--accent-emerald-text))' : 'hsl(var(--accent-cyan-text))',
                fontWeight: 800,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              Rank #{activeSpectrumItem.rankIndex}: {activeSpectrumItem.venue.name} ({formatSpectrumPkr(activeSpectrumItem.peakRentalPkr)})
            </span>
          ) : (
            <span style={{ color: 'hsl(var(--text-secondary))' }} className="num-tabular">
              Avg: {formatSpectrumPkr(avgRental)}
            </span>
          )}
          <span style={{ color: 'hsl(var(--accent-gold-text))' }} className="num-tabular">
            High: {formatSpectrumPkr(maxRental)}
          </span>
        </div>

        <div className="price-spectrum-track">
          {activeSpectrumItem && (
            <div
              className={`price-spectrum-callout ${activeSpectrumItem.isLow ? 'lowest' : ''}`.trim()}
              style={{
                left: `${Math.min(86, Math.max(14, activeSpectrumItem.rawPct))}%`
              }}
              role="status"
            >
              <span className="num-tabular">
                #{activeSpectrumItem.rankIndex} {activeSpectrumItem.venue.name} • {formatSpectrumPkr(activeSpectrumItem.peakRentalPkr)}
              </span>
            </div>
          )}

          {spectrumItems.map((item) => {
            const isMarkerActive = activeSpectrumVenueId === item.venue.id;
            return (
              <div
                key={item.venue.id}
                role="button"
                tabIndex={0}
                aria-label={`${item.venue.name}: Rs. ${item.peakRentalPkr.toLocaleString()} (Rank ${item.rankIndex})`}
                aria-pressed={isMarkerActive}
                className={`price-spectrum-marker ${item.isLow ? 'lowest' : ''} ${isMarkerActive ? 'active-connection' : ''} ${activeSpectrumItem && !isMarkerActive ? 'dimmed-connection' : ''}`.trim()}
                style={{ left: `${item.rawPct}%` }}
                title={`${item.venue.name}: Rs. ${item.peakRentalPkr.toLocaleString()}`}
                onMouseEnter={() => setHighlightedVenueId(item.venue.id)}
                onMouseLeave={() => setHighlightedVenueId((prev) => (prev === item.venue.id ? null : prev))}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  lastTouchMarkerRef.current = { id: item.venue.id, time: Date.now() };
                  setHighlightedVenueId(item.venue.id);
                  scrollCardIntoHorizontalView(item.venue.id);
                }}
                onFocus={() => setHighlightedVenueId(item.venue.id)}
                onBlur={() => setHighlightedVenueId((prev) => (prev === item.venue.id ? null : prev))}
                onClick={(e) => {
                  e.stopPropagation();
                  const recentTouch = lastTouchMarkerRef.current;
                  if (recentTouch && recentTouch.id === item.venue.id && Date.now() - recentTouch.time < 480) {
                    setHighlightedVenueId(item.venue.id);
                    return;
                  }
                  setHighlightedVenueId((prev) => {
                    const next = prev === item.venue.id ? null : item.venue.id;
                    if (next) scrollCardIntoHorizontalView(next);
                    return next;
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setHighlightedVenueId((prev) => {
                      const next = prev === item.venue.id ? null : item.venue.id;
                      if (next) scrollCardIntoHorizontalView(next);
                      return next;
                    });
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Side-by-Side Hall Rental Comparison Cards (Desktop Grid + Mobile Horizontal Momentum Scroller) */}
      <div
        className="grid-cards mobile-momentum-1row price-comparison-grid"
        style={
          {
            '--desktop-compare-cols': Math.min(sortedRentals.length, 4),
            maxHeight: 'none'
          } as React.CSSProperties
        }
      >
        {sortedRentals.map((c, idx) => {
          const isLowestRental = idx === 0 && c.peakRentalPkr === minRental;
          const isVoiceSpotlighted = voiceSpotlightVenueId === c.venue.id;
          const isSpectrumHighlighted = activeSpectrumVenueId === c.venue.id;
          const rankIndex = idx + 1;
          const diffVsBest = c.peakRentalPkr - minRental;
          const savingsVsMax = maxRental - c.peakRentalPkr;
          const rentalPct = Math.round((c.peakRentalPkr / maxRental) * 100);

          const handleCardSelect = () => {
            setHighlightedVenueId(c.venue.id);
            if (onSelectVenue) {
              onSelectVenue(c.venue, c.activePkgObj);
            } else {
              if (onTouchAction) {
                onTouchAction('select_compared_venue', {
                  venueId: c.venue.id,
                  venueName: c.venue.name,
                  peakRentalPkr: c.peakRentalPkr
                });
              }
              setFocusedVenueId(c.venue.id);
            }
          };

          return (
            <ConicGlowCard
              key={c.venue.id}
              className={`venue-card composable-stage-card ${isVoiceSpotlighted ? 'voice-spotlight-card' : ''} ${isSpectrumHighlighted ? 'spectrum-highlighted-card active-glow' : ''}`.trim()}
              glowIntensity={isSpectrumHighlighted ? 0.96 : isVoiceSpotlighted ? 0.92 : isLowestRental ? 0.72 : 0.55}
              active={isLowestRental || isVoiceSpotlighted}
              glowVariant={isLowestRental ? 'emerald' : 'gemini'}
              ariaLabel={`Compare Hall Rental for ${c.venue.name} in ${c.venue.area}`}
              style={
                {
                  '--card-index': idx,
                  ...(typeof window !== 'undefined' && window.innerWidth < 900
                    ? {}
                    : { viewTransitionName: `compare-card-${c.venue.id.replace(/[^a-zA-Z0-9_-]/g, '-')}` })
                } as React.CSSProperties
              }
              onMouseEnter={() => setHighlightedVenueId(c.venue.id)}
              onMouseLeave={() => setHighlightedVenueId((prev) => (prev === c.venue.id ? null : prev))}
              onTouchStart={() => setHighlightedVenueId(c.venue.id)}
              onFocus={() => setHighlightedVenueId(c.venue.id)}
              onBlur={() => setHighlightedVenueId((prev) => (prev === c.venue.id ? null : prev))}
              onClick={handleCardSelect}
            >
              <div data-comparison-venue-id={c.venue.id} style={{ padding: '0.88rem 0.95rem', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', gap: '0.45rem' }}>
                <div>
                  {/* 1. Area & Venue Type + Lowest Rental Winner Badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.42rem', gap: '0.35rem', flexWrap: 'wrap' }}>
                    <span className="pill-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.66rem', padding: '0.16rem 0.52rem' }}>
                      <MapPinIcon size={11} style={{ color: 'hsl(var(--glow-gemini-cyan))' }} />
                      <span>
                        {c.venue.area} • {formatVenueType(c.venue.venueType)}
                      </span>
                    </span>

                    {isLowestRental ? (
                      <span className="pill-badge verified num-tabular" style={{ fontSize: '0.66rem', fontWeight: 800, padding: '0.16rem 0.52rem' }}>
                        <TrophyIcon size={11} />
                        <span>
                          Lowest Rental{savingsVsMax > 0 ? ` • Saves Rs. ${savingsVsMax.toLocaleString()}` : ''}
                        </span>
                      </span>
                    ) : (
                      <span className="pill-badge pricing num-tabular" style={{ fontSize: '0.66rem', padding: '0.16rem 0.52rem' }}>
                        <span>
                          Rank {rankIndex} • {diffVsBest > 0 ? `+Rs. ${diffVsBest.toLocaleString()}` : 'Same Rental'}
                        </span>
                      </span>
                    )}
                  </div>

                  {/* 2. Venue Title, Capacity & Peak Hall Rental Callout */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.42rem' }}>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ fontSize: '1.02rem', fontWeight: 800, fontFamily: 'var(--font-display)', lineHeight: 1.18 }}>
                        {c.venue.name}
                      </h3>
                      <p style={{ fontSize: '0.71rem', color: 'hsl(var(--text-secondary))', marginTop: '0.14rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.28rem', flexWrap: 'wrap' }}>
                        <UsersIcon size={12} style={{ color: 'hsl(var(--glow-gemini-blue))' }} />
                        <span>{c.venue.minCapacity}–{c.venue.maxCapacity} Pax •</span>
                        <StarIcon size={11} style={{ color: 'hsl(var(--accent-gold))' }} />
                        <span>{c.venue.googleRating.toFixed(1)}</span>
                      </p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.62rem', color: c.hasExplicitRental ? 'hsl(var(--accent-emerald-text))' : 'hsl(var(--accent-cyan-text))', fontWeight: 700, textTransform: 'uppercase' }}>
                        {c.hasExplicitRental ? 'Peak Hall Rental' : 'Area Market Est.'}
                      </div>
                      <div className="num-tabular" style={{ fontSize: '1.1rem', fontWeight: 800, color: 'hsl(var(--accent-gold))' }}>
                        {c.hasExplicitRental ? '' : '~'}Rs. {c.peakRentalPkr.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* 3. Proportional Hall Rental Bar (Syncs with Google Flights Price Spectrum Meter) */}
                  <div
                    className={`card-proportion-box ${isSpectrumHighlighted ? 'spectrum-connected' : ''}`.trim()}
                    style={{
                      padding: '0.4rem 0.6rem',
                      borderRadius: '0.6rem',
                      backgroundColor: isSpectrumHighlighted
                        ? 'hsl(var(--glow-gemini-blue) / 0.14)'
                        : 'hsl(var(--surface-elevated) / 0.7)',
                      border: isSpectrumHighlighted
                        ? '1px solid hsl(var(--glow-gemini-cyan) / 0.8)'
                        : '1px solid hsl(var(--surface-border) / 0.55)',
                      marginBottom: '0.42rem',
                      transition: 'background-color 0.22s ease, border-color 0.22s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: 'hsl(var(--text-secondary))', marginBottom: '0.22rem' }}>
                      <span>Hall Rental Proportion ({rentalPct}%)</span>
                      <span
                        className="num-tabular"
                        style={{
                          fontWeight: 700,
                          color: isSpectrumHighlighted ? 'hsl(var(--accent-cyan-text))' : 'hsl(var(--text-primary))'
                        }}
                      >
                        {isSpectrumHighlighted ? `Rank #${rankIndex} on Price Bar` : 'Hall Space Only'}
                      </span>
                    </div>
                    <div style={{ width: '100%', height: '0.44rem', borderRadius: '9999px', backgroundColor: 'hsl(var(--surface-border) / 0.38)', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${rentalPct}%`,
                          height: '100%',
                          background: isLowestRental
                            ? 'linear-gradient(90deg, hsl(var(--accent-emerald)), hsl(var(--glow-gemini-cyan)))'
                            : 'linear-gradient(90deg, hsl(var(--glow-gemini-blue)), hsl(var(--accent-gold)))',
                          transition: 'width 0.35s ease'
                        }}
                      />
                    </div>
                  </div>

                  {/* 4. 3-Row Hall Comparison: Peak Season Hall Rental, Off-Peak / Weekday Hall Rental, Guest Capacity & Space */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.42rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.36rem' }}>
                      <div
                        style={{
                          padding: '0.36rem 0.52rem',
                          borderRadius: '0.55rem',
                          backgroundColor: 'hsl(var(--surface-elevated) / 0.78)',
                          border: '1px solid hsl(var(--glow-gemini-blue) / 0.45)'
                        }}
                      >
                        <div style={{ fontSize: '0.61rem', color: 'hsl(var(--text-secondary))', fontWeight: 600 }}>
                          Peak Season (Nov–Feb)
                        </div>
                        <div className="num-tabular" style={{ fontSize: '0.78rem', fontWeight: 800, color: 'hsl(var(--accent-gold))', marginTop: '0.06rem' }}>
                          {c.hasExplicitRental ? '' : '~'}Rs. {c.peakRentalPkr.toLocaleString()}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: '0.36rem 0.52rem',
                          borderRadius: '0.55rem',
                          backgroundColor: 'hsl(var(--accent-emerald) / 0.1)',
                          border: '1px solid hsl(var(--accent-emerald) / 0.4)'
                        }}
                      >
                        <div style={{ fontSize: '0.61rem', color: 'hsl(var(--accent-emerald-text))', fontWeight: 700 }}>
                          Off-Peak / Weekday
                        </div>
                        <div className="num-tabular" style={{ fontSize: '0.78rem', fontWeight: 800, color: 'hsl(var(--text-primary))', marginTop: '0.06rem' }}>
                          ~Rs. {c.offPeakRentalPkr.toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.32rem 0.55rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'hsl(var(--surface-elevated) / 0.55)',
                        border: '1px solid hsl(var(--surface-border) / 0.55)',
                        fontSize: '0.66rem'
                      }}
                    >
                      <span style={{ color: 'hsl(var(--text-secondary))', fontWeight: 600 }}>Guest Capacity &amp; Space</span>
                      <span className="num-tabular" style={{ color: 'hsl(var(--text-primary))', fontWeight: 700 }}>
                        {c.venue.minCapacity}–{c.venue.maxCapacity} Guests • {formatVenueType(c.venue.venueType)}
                      </span>
                    </div>
                  </div>

                  {/* Optional Custom Focus Metrics when requested by Leda */}
                  {focusMetrics && focusMetrics.length > 0 && (
                    <div
                      style={{
                        padding: '0.34rem 0.55rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'hsl(var(--glow-gemini-blue) / 0.1)',
                        border: '1px solid hsl(var(--glow-gemini-blue) / 0.35)',
                        marginBottom: '0.42rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.15rem'
                      }}
                    >
                      {focusMetrics.slice(0, 2).map((m, mIdx) => {
                        const resolved = resolveCompareMetricValue(c.venue, m, c.peakRentalPkr, c.offPeakRentalPkr);
                        return (
                          <div key={mIdx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.66rem' }}>
                            <span style={{ color: 'hsl(var(--text-secondary))', fontWeight: 600 }}>{resolved.label}:</span>
                            <span className="num-tabular" style={{ color: 'hsl(var(--accent-cyan-text))', fontWeight: 800 }}>{resolved.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* 5. Core Hall Rental Inclusions (3 Clean Rows with Pure Line Icons) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.22rem', fontSize: '0.68rem', color: 'hsl(var(--text-secondary))' }}>
                    {c.hallInclusions.map((inc, iIdx) => (
                      <div key={iIdx} style={{ display: 'flex', alignItems: 'center', gap: '0.32rem' }}>
                        {iIdx === 1 ? (
                          <ZapIcon size={11} style={{ color: 'hsl(var(--accent-gold))', flexShrink: 0 }} />
                        ) : (
                          <CheckIcon size={11} style={{ color: 'hsl(var(--accent-emerald))', flexShrink: 0 }} />
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Clean Minimal Interactive Footer (Full Card Clickable) */}
                <div
                  style={{
                    borderTop: '1px solid hsl(var(--porcelain-50) / 0.07)',
                    paddingTop: '0.44rem',
                    marginTop: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.7rem',
                    color: 'hsl(var(--text-secondary))'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontWeight: 500 }}>
                    <CalendarIcon size={11} style={{ color: 'hsl(var(--glow-gemini-cyan))' }} />
                    <span>Slot Availability</span>
                  </span>
                  <span className="pill-badge pricing num-tabular" style={{ fontSize: '0.68rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.16rem 0.5rem' }}>
                    <span>Rs. {(c.activePkgObj?.pricePkr || c.peakRentalPkr).toLocaleString()}</span>
                    <ArrowRightIcon size={10} />
                  </span>
                </div>
              </div>
            </ConicGlowCard>
          );
        })}
      </div>

      {/* Focused Single-Card Hall Rental Detail Modal */}
      {focusedEntry && (
        <div className="modal-backdrop" onClick={() => setFocusedVenueId(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: '36rem', padding: '1.4rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
              <div>
                <span className="pill-badge verified" style={{ marginRight: '0.5rem' }}>
                  {focusedEntry.venue.area} • {formatVenueType(focusedEntry.venue.venueType)}
                </span>
                <strong style={{ fontSize: '1.12rem' }}>{focusedEntry.venue.name}</strong>
              </div>
              <button
                type="button"
                className="dock-action-btn"
                onClick={() => setFocusedVenueId(null)}
                aria-label="Close Hall Rental Breakdown"
              >
                <CloseIcon size={16} />
              </button>
            </div>

            <div
              style={{
                padding: '0.95rem 1.1rem',
                borderRadius: '0.8rem',
                backgroundColor: 'hsl(var(--surface-card) / 0.9)',
                border: '1px solid hsl(var(--surface-border))',
                marginBottom: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.45rem',
                fontSize: '0.84rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'hsl(var(--text-secondary))' }}>Peak Season Hall Rental:</span>
                <span className="num-tabular" style={{ fontWeight: 800, color: 'hsl(var(--accent-gold))' }}>
                  Rs. {focusedEntry.peakRentalPkr.toLocaleString()} PKR
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'hsl(var(--text-secondary))' }}>Off-Peak / Weekday Hall Rental:</span>
                <span className="num-tabular" style={{ fontWeight: 800, color: 'hsl(var(--accent-emerald-text))' }}>
                  Rs. {focusedEntry.offPeakRentalPkr.toLocaleString()} PKR
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'hsl(var(--text-secondary))' }}>Guest Capacity:</span>
                <span className="num-tabular" style={{ fontWeight: 700 }}>
                  {focusedEntry.venue.minCapacity}–{focusedEntry.venue.maxCapacity} Guests
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button type="button" className="btn-secondary" onClick={() => setFocusedVenueId(null)}>
                <span>Close</span>
              </button>
              {onSelectVenue && (
                <button
                  type="button"
                  className="btn-primary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    const v = focusedEntry.venue;
                    const pkg = focusedEntry.activePkgObj;
                    setFocusedVenueId(null);
                    onSelectVenue(v, pkg);
                  }}
                >
                  <CalendarIcon size={15} />
                  <span>Select &amp; Check Slot Availability</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
